import { getDocs, limit, query, startAfter } from "firebase/firestore";

export const INITIAL_RESULTS_COUNT = 10;

const CLIENT_FILTER_BATCH_MULTIPLIER = 5;

export function isNullishSubjectId(v) {
  if (v === undefined || v === null) return true;
  const s = String(v).trim();
  return s.length === 0;
}

function getBatchLimit(remainingNeeded, hasClientFilter) {
  const safeRemaining = Math.max(remainingNeeded, 1);

  if (!hasClientFilter) {
    return safeRemaining;
  }

  return Math.max(
    safeRemaining * CLIENT_FILTER_BATCH_MULTIPLIER,
    INITIAL_RESULTS_COUNT * CLIENT_FILTER_BATCH_MULTIPLIER,
  );
}

export function getInitialRealtimeListenLimit(pageSize, hasClientFilter) {
  const targetCount = Math.max(pageSize, 0);
  if (targetCount === 0) return 0;

  const initialTarget = Math.min(INITIAL_RESULTS_COUNT, targetCount);
  return hasClientFilter
    ? getBatchLimit(initialTarget, true)
    : initialTarget;
}

export function getNextRealtimeListenLimit(
  currentLimit,
  pageSize,
  hasClientFilter,
) {
  const targetCount = Math.max(pageSize, 0);
  if (targetCount === 0) return currentLimit;

  if (!hasClientFilter) {
    return Math.max(currentLimit, targetCount);
  }

  const minimumTargetLimit = getBatchLimit(targetCount, true);
  if (currentLimit < minimumTargetLimit) {
    return minimumTargetLimit;
  }

  return currentLimit + INITIAL_RESULTS_COUNT * CLIENT_FILTER_BATCH_MULTIPLIER;
}

export function selectRealtimeMeasurementDocs(
  snapshotDocs,
  pageSize,
  clientFilter = null,
) {
  const targetCount = Math.max(pageSize, 0);
  if (targetCount === 0) return [];

  const selected = [];

  for (const doc of snapshotDocs) {
    if (clientFilter) {
      const data = doc.data() || {};
      if (!clientFilter(data)) continue;
    }

    selected.push(doc);
    if (selected.length >= targetCount) break;
  }

  return selected;
}

export function shouldExpandRealtimeListenLimit(
  snapshotSize,
  listenLimit,
  selectedCount,
  pageSize,
) {
  return (
    pageSize > 0 &&
    selectedCount < pageSize &&
    snapshotSize >= listenLimit
  );
}

export async function loadStagedMeasurementDocs({
  colRef,
  pageSize,
  baseConstraints = [],
  clientFilter = null,
  onStage = null,
}) {
  const targetCount = Math.max(pageSize, 0);
  const initialTarget = Math.min(INITIAL_RESULTS_COUNT, targetCount);
  const docs = [];
  const seen = new Set();
  let cursor = null;
  let initialEmitted = initialTarget === 0;

  while (docs.length < targetCount) {
    const remainingNeeded = initialEmitted
      ? targetCount - docs.length
      : initialTarget - docs.length;
    const batchLimit = getBatchLimit(remainingNeeded, !!clientFilter);
    const qy = cursor
      ? query(colRef, ...baseConstraints, startAfter(cursor), limit(batchLimit))
      : query(colRef, ...baseConstraints, limit(batchLimit));
    const snap = await getDocs(qy);

    if (snap.empty) break;

    cursor = snap.docs[snap.docs.length - 1] ?? null;

    for (let index = 0; index < snap.docs.length; index += 1) {
      const doc = snap.docs[index];
      if (seen.has(doc.id)) continue;

      if (clientFilter) {
        const data = doc.data() || {};
        if (!clientFilter(data)) continue;
      }

      seen.add(doc.id);
      docs.push(doc);

      if (!initialEmitted && docs.length >= initialTarget) {
        const hasMoreToLoad =
          docs.length < targetCount &&
          (index < snap.docs.length - 1 || snap.docs.length === batchLimit);
        onStage?.(docs.slice(0, initialTarget), { hasMoreToLoad });
        initialEmitted = true;
      }

      if (docs.length >= targetCount) break;
    }

    if (snap.docs.length < batchLimit || !cursor) {
      break;
    }
  }

  if (!initialEmitted) {
    onStage?.(docs.slice(0, targetCount), { hasMoreToLoad: false });
  }

  return docs.slice(0, targetCount);
}
